import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Legend, ComposedChart } from "recharts";
import { api, brl, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useFeatures } from "@/lib/features";
import { PageHead, Stat, Empty, RangePicker, isoDate, todayIso } from "@/components/erp/ErpUi";
import { Gauge, Loader2, AlertTriangle, PackageMinus, Car, ArrowLeft, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [["mes", "Mês atual"], ["anterior", "Mês anterior"], ["3m", "Últimos 3 meses"], ["6m", "Últimos 6 meses"], ["12m", "Últimos 12 meses"], ["custom", "Personalizado"]];
const MLBL = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const ml = (s) => `${MLBL[Number(s.slice(5, 7)) - 1]}/${s.slice(2, 4)}`;
const kfmt = (v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(Math.round(v)));
const TT = { contentStyle: { background: "#09090b", border: "1px solid #27272a", borderRadius: 8, fontSize: 12 }, formatter: (v, n) => [n === "Margem %" ? `${v}%` : brl(v), n] };

function rangeFor(p) {
  const d = new Date();
  const first = (y, m) => new Date(y, m, 1);
  if (p === "anterior") { return [isoDate(first(d.getFullYear(), d.getMonth() - 1)), isoDate(new Date(d.getFullYear(), d.getMonth(), 0))]; }
  const back = { "3m": 2, "6m": 5, "12m": 11 }[p] || 0;
  return [isoDate(first(d.getFullYear(), d.getMonth() - back)), todayIso()];
}

function Card({ title, children, testid, className = "" }) {
  return (
    <section data-testid={testid} className={`rounded-2xl border border-card2-border bg-card2 p-4 sm:p-5 min-w-0 ${className}`}>
      <h2 className="font-display text-sm sm:text-base font-bold uppercase text-zinc-100 mb-3">{title}</h2>
      {children}
    </section>
  );
}

function Rank({ rows, testid, sub }) {
  if (!rows?.length) { return <p className="text-sm text-zinc-500">Sem dados no período.</p>; }
  const max = Math.max(...rows.map((r) => r.value)) || 1;
  return (
    <ol data-testid={testid} className="space-y-2">
      {rows.map((r, i) => (
        <li key={`${r.name}-${i}`} className="text-sm">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-zinc-200"><span className="text-zinc-500 tabular-nums mr-1">{i + 1}.</span>{r.name}</span>
            <span className="tabular-nums font-bold text-zinc-100 shrink-0">{brl(r.value)}</span>
          </div>
          <div className="mt-1 h-1.5 rounded-full bg-zinc-800 overflow-hidden"><div className="h-full rounded-full bg-green-brand" style={{ width: `${(r.value / max) * 100}%` }} /></div>
          {sub && r.sub && <div className="text-[11px] text-zinc-500 mt-0.5">{r.sub}</div>}
        </li>
      ))}
    </ol>
  );
}

function TopCardsRow({ d }) {
  const goal = d.month.goal || 0;
  const pct = goal ? Math.min(100, (d.month.revenue / goal) * 100) : 0;
  const left = Math.max(0, goal - d.month.revenue);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <Stat testid="panel-today" label="Faturamento do dia" value={brl(d.today.revenue)} tone="green" />
      <Stat testid="panel-month" label="Faturamento do mês" value={brl(d.month.revenue)} />
      <div data-testid="panel-goal" className="rounded-2xl border border-card2-border bg-card2 p-4 min-w-0">
        <div className="text-[11px] font-mono-caps uppercase text-zinc-500">Meta do mês</div>
        {goal ? (
          <>
            <div className="font-display text-xl sm:text-2xl font-black mt-1 text-zinc-100">{brl(goal)}</div>
            <div className="mt-2 h-2.5 rounded-full bg-zinc-800 overflow-hidden"><div data-testid="panel-goal-bar" className="h-full rounded-full bg-green-brand transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
            <div data-testid="panel-goal-left" className="text-xs text-zinc-400 mt-1">{pct.toFixed(0)}% · {left > 0 ? `faltam ${brl(left)}` : "meta batida!"}</div>
          </>
        ) : <div className="text-sm text-zinc-500 mt-2">Sem meta. <Link to="/owner/settings" className="text-green-neon underline">Definir meta</Link></div>}
      </div>
    </div>
  );
}

function Charts({ d }) {
  const s = d.series.map((x) => ({ ...x, label: ml(x.month) }));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <Card title="Faturamento — últimos 12 meses" testid="panel-chart-revenue" className="lg:col-span-2">
        <div className="h-64"><ResponsiveContainer><BarChart data={s}><CartesianGrid stroke="#27272a" vertical={false} /><XAxis dataKey="label" stroke="#71717a" fontSize={11} /><YAxis stroke="#71717a" fontSize={11} tickFormatter={kfmt} width={40} /><Tooltip {...TT} cursor={{ fill: "#ffffff08" }} /><Bar dataKey="revenue" name="Faturamento" fill="#16a34a" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer></div>
      </Card>
      <Card title="Lucro e margem por mês" testid="panel-chart-profit">
        <div className="h-64"><ResponsiveContainer><ComposedChart data={s}><CartesianGrid stroke="#27272a" vertical={false} /><XAxis dataKey="label" stroke="#71717a" fontSize={11} /><YAxis yAxisId="l" stroke="#71717a" fontSize={11} tickFormatter={kfmt} width={40} /><YAxis yAxisId="r" orientation="right" stroke="#71717a" fontSize={11} unit="%" width={36} /><Tooltip {...TT} /><Legend wrapperStyle={{ fontSize: 11 }} /><Bar yAxisId="l" dataKey="profit" name="Lucro" fill="#22c55e" radius={[4, 4, 0, 0]} /><Line yAxisId="r" dataKey="margin" name="Margem %" stroke="#f59e0b" strokeWidth={2} dot={false} /></ComposedChart></ResponsiveContainer></div>
      </Card>
      <Card title="Serviços x peças" testid="panel-chart-mix">
        <div className="h-64"><ResponsiveContainer><LineChart data={s}><CartesianGrid stroke="#27272a" vertical={false} /><XAxis dataKey="label" stroke="#71717a" fontSize={11} /><YAxis stroke="#71717a" fontSize={11} tickFormatter={kfmt} width={40} /><Tooltip {...TT} /><Legend wrapperStyle={{ fontSize: 11 }} /><Line dataKey="services" name="Serviços" stroke="#22c55e" strokeWidth={2} dot={false} /><Line dataKey="parts" name="Peças" stroke="#38bdf8" strokeWidth={2} dot={false} /></LineChart></ResponsiveContainer></div>
        <p data-testid="panel-mix-period" className="text-xs text-zinc-400 mt-2">No período: serviços {brl(d.period.services)} · peças {brl(d.period.parts)}</p>
      </Card>
      <Card title="Orçamentos — gerados vs aprovados" testid="panel-chart-budgets">
        <div className="h-64"><ResponsiveContainer><BarChart data={s}><CartesianGrid stroke="#27272a" vertical={false} /><XAxis dataKey="label" stroke="#71717a" fontSize={11} /><YAxis stroke="#71717a" fontSize={11} tickFormatter={kfmt} width={40} /><Tooltip {...TT} /><Legend wrapperStyle={{ fontSize: 11 }} /><Bar dataKey="budgets_total" name="Gerados" fill="#64748b" radius={[4, 4, 0, 0]} /><Bar dataKey="budgets_approved" name="Aprovados" fill="#22c55e" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer></div>
        <p data-testid="panel-budgets-lost" className="text-xs text-zinc-400 mt-2">Deixaram de fechar no período: {brl(s.reduce((a, x) => a + (x.budgets_lost || 0), 0))}</p>
      </Card>
    </div>
  );
}

function Alerts({ d, low, sug, feats, followUps }) {
  const todayIsoStr = new Date().toISOString().slice(0, 10);
  const todayFollow = (followUps || []).filter((f) => f.when === todayIsoStr);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
      {!!todayFollow.length && (
        <Card title="Orçamentos para retomar hoje" testid="panel-alert-followup">
          <Link to="/owner/lembretes" className="flex items-center gap-2 text-sm text-amber-200 hover:underline"><CalendarClock className="h-4 w-4 text-amber-400" /><b className="font-display text-lg">{todayFollow.length}</b> cliente(s) agendados para contato hoje</Link>
          <ul className="mt-2 space-y-1 text-xs">
            {todayFollow.slice(0, 4).map((f) => (<li key={`${f.order_id}-${f.when}-${f.scope}`} className="text-zinc-300 truncate">{f.customer_name}{f.vehicle_plate ? ` · ${f.vehicle_plate}` : ""}{f.note ? ` · ${f.note}` : ""}</li>))}
          </ul>
        </Card>
      )}
      {feats.enabled("relatorios.margem_baixa") && (
        <Card title="Margem baixa" testid="panel-alert-margin">
          {low ? <Link to="/owner/margem-baixa" className="flex items-center gap-2 text-sm text-amber-900 dark:text-amber-200 hover:underline"><AlertTriangle className="h-4 w-4 text-amber-700 dark:text-amber-400" /><b className="font-display text-lg">{low.count}</b> OS/vendas abaixo de {low.min_margin_pct}%</Link> : <p className="text-sm text-zinc-500">—</p>}
        </Card>
      )}
      {feats.enabled("estoque.sugestao") && (
        <Card title="Estoque acabando" testid="panel-alert-stock">
          {!sug?.length ? <p className="text-sm text-zinc-500">Nada para repor agora.</p> : (
            <ul className="space-y-1 text-sm">
              {sug.slice(0, 5).map((s) => <li key={s.product_id} className="flex justify-between gap-2"><span className="truncate text-zinc-200"><PackageMinus className="inline h-3.5 w-3.5 mr-1 text-red-300" />{s.description}</span><span className="text-zinc-400 shrink-0">{s.stock ?? 0} un</span></li>)}
              <li><Link to="/owner/sugestao-compra" className="text-xs text-green-neon underline">Ver sugestão de compra ({sug.length})</Link></li>
            </ul>
          )}
        </Card>
      )}
      <Card title={`Carros parados no pátio (+${d.stale_days} dias)`} testid="panel-alert-stale">
        {!d.stale_cars.length ? <p className="text-sm text-zinc-500">Nenhum carro parado.</p> : (
          <ul className="space-y-1 text-sm">
            {d.stale_cars.slice(0, 6).map((c) => <li key={c.id}><Link to={`/owner/ordens/${c.id}`} className="flex justify-between gap-2 hover:underline"><span className="truncate text-zinc-200"><Car className="inline h-3.5 w-3.5 mr-1 text-amber-300" />{c.plate || `OS #${c.number}`} · {c.status}</span><span className="text-amber-300 shrink-0">{c.days}d</span></Link></li>)}
            {d.stale_count > 6 && <li className="text-xs text-zinc-500">+{d.stale_count - 6} outros</li>}
          </ul>
        )}
      </Card>
    </div>
  );
}

export default function OwnerPanel() {
  const { user } = useAuth();
  const feats = useFeatures();
  const [preset, setPreset] = useState("mes");
  const [[from, to], setRange] = useState(rangeFor("mes"));
  const [d, setD] = useState(null);
  const [team, setTeam] = useState(null);
  const [low, setLow] = useState(null);
  const [sug, setSug] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const lowOn = feats.enabled("relatorios.margem_baixa");
  const sugOn = feats.enabled("estoque.sugestao");
  const pick = (p) => { setPreset(p); if (p !== "custom") { setRange(rangeFor(p)); } };
  useEffect(() => {
    if (!from || !to) { return; }
    setD(null);
    api.get("/owner/panel", { params: { from, to } }).then(({ data }) => setD(data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/team/report", { params: { from, to } }).then(({ data }) => setTeam(data)).catch(() => setTeam(null));
    if (lowOn) { api.get("/erp/low-margin", { params: { from, to } }).then(({ data }) => setLow(data)).catch(() => setLow(null)); }
  }, [from, to, user?.shop_id, lowOn]);
  useEffect(() => {
    if (!sugOn) { return; }
    api.get("/erp/purchase-suggestions").then(({ data }) => setSug(Array.isArray(data) ? data : data.items || [])).catch(() => setSug(null));
  }, [user?.shop_id, sugOn]);
  useEffect(() => {
    api.get("/crm/reminders").then(({ data }) => setFollowUps(data?.follow_ups || [])).catch(() => setFollowUps([]));
  }, [user?.shop_id]);
  const ranks = useMemo(() => ({
    mech: (team?.mechanics || []).filter((x) => x.name !== "Não informado").slice(0, 5).map((x) => ({ name: x.name, value: x.revenue, sub: `${x.orders} OS` })),
    cons: (team?.consultants || []).filter((x) => x.name !== "Não informado").slice(0, 5).map((x) => ({ name: x.name, value: x.revenue, sub: `${x.orders} OS` })),
    serv: (d?.top_services || []).slice(0, 5).map((x) => ({ name: x.description || "Serviço", value: x.revenue, sub: `${Number(x.qty).toLocaleString("pt-BR")} vendidos` })),
    cust: (d?.top_customers || []).slice(0, 5).map((x) => ({ name: x.name, value: x.total, sub: `${x.count} compra(s)` })),
  }), [team, d]);
  return (
    <div data-testid="owner-panel" className="space-y-6 animate-fade-up">
      <PageHead icon={Gauge} kicker="Relatórios" title="Painel de indicadores">
        <Link to="/owner" data-testid="panel-back" className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-zinc-100"><ArrowLeft className="h-4 w-4" /> Central</Link>
      </PageHead>
      <div data-testid="panel-filter" className="flex flex-wrap items-center gap-2">
        {PRESETS.map(([k, l]) => (
          <button key={k} type="button" data-testid={`panel-preset-${k}`} onClick={() => pick(k)}
                  className={`h-9 px-3 rounded-full border text-xs font-bold transition-colors ${preset === k ? "border-green-neon bg-green-brand/15 text-green-neon" : "border-zinc-800 text-zinc-300 hover:bg-zinc-900"}`}>{l}</button>
        ))}
        {preset === "custom" && <RangePicker from={from} to={to} onChange={setRange} testid="panel-range" />}
      </div>
      {!d ? <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-500" /></div> : (
        <>
          <TopCardsRow d={d} />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat testid="panel-period-revenue" label="Faturamento no período" value={brl(d.period.revenue)} />
            <Stat testid="panel-period-profit" label="Lucro no período" value={brl(d.period.profit)} tone={d.period.profit >= 0 ? "green" : "red"} />
            <Stat testid="panel-period-margin" label="Margem" value={`${d.period.margin}%`} tone="amber" />
            <Stat testid="panel-period-expenses" label="Despesas" value={brl(d.period.expenses)} />
          </div>
          <Charts d={d} />
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            <Card title="Mecânicos que mais faturaram" testid="panel-rank-mechanics"><Rank rows={ranks.mech} testid="rank-mechanics" sub /></Card>
            <Card title="Consultores que mais faturaram" testid="panel-rank-consultants"><Rank rows={ranks.cons} testid="rank-consultants" sub /></Card>
            <Card title="Serviços mais vendidos" testid="panel-rank-services"><Rank rows={ranks.serv} testid="rank-services" sub /></Card>
            <Card title="Clientes que mais gastam" testid="panel-rank-customers"><Rank rows={ranks.cust} testid="rank-customers" sub /></Card>
          </div>
          <Alerts d={d} low={low} sug={sug} feats={feats} followUps={followUps} />
          {!d.series.some((x) => x.revenue) && <Empty>Ainda não há faturamento registrado nos últimos 12 meses.</Empty>}
        </>
      )}
    </div>
  );
}
